/**
 * Logo Video Mask Reveal — scroll-driven animation.
 *
 * The user places a simple placeholder in Ghost admin:
 *   <section id="logo-video-section" class="logo-video-section"></section>
 *
 * This script relocates the section to the end of #site-main synchronously
 * (before ScrollTrigger), builds the video layer, waits until the video can
 * play, then initialises GSAP. The site loader stays until this finishes
 * (coordinated with hero-frame-animation.js via __yessLogoVideoReady).
 *
 * Dependencies: GSAP 3 + ScrollTrigger
 */
(function () {
  "use strict";

  function signalLogoVideoReady() {
    window.__yessLogoVideoReady = true;
    if (typeof window.__yessTryDismissSiteLoader === "function") {
      window.__yessTryDismissSiteLoader();
    }
  }

  function run() {
    var section = document.getElementById("logo-video-section");
    if (!section) return;

    /* Move before reviews section (or fallback) */
    var main = document.getElementById("site-main");
    if (main) {
      var reviewsSec = document.querySelector(".reviews-card-main, .reviews-container");
      if (reviewsSec) {
        var langWrap = reviewsSec.closest && reviewsSec.closest("[data-yes-lang]");
        var anchor = langWrap || reviewsSec;
        if (anchor.parentNode) {
          anchor.parentNode.insertBefore(section, anchor);
        }
      } else if (!section.parentNode) {
        main.appendChild(section);
      }
    }

    if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
      console.warn("[logo-video] GSAP or ScrollTrigger not loaded");
      section.classList.add("logo-video-section--ready");
      signalLogoVideoReady();
      return;
    }
    gsap.registerPlugin(ScrollTrigger);

    /* ── Auto-detect theme asset base path ── */
    var ASSET_BASE = (function () {
      var tags = document.querySelectorAll(
        'script[src*="/assets/"],link[href*="/assets/"]'
      );
      for (var i = 0; i < tags.length; i++) {
        var url = tags[i].src || tags[i].href || "";
        var idx = url.indexOf("/assets/");
        if (idx >= 0) return url.substring(0, idx + 8);
      }
      return "/assets/";
    })();

    var videoSrc =
      section.getAttribute("data-video") ||
      ASSET_BASE + "images/video/smart-city-video.mp4";
    var logoSrc = ASSET_BASE + "images/Yes-logo-section.svg";

    /* ── Build inner elements if not already present ── */
    if (!section.querySelector(".logo-video-bg")) {
      var video = document.createElement("video");
      video.className = "logo-video-bg";
      video.id = "logo-video-player";
      video.autoplay = true;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute("playsinline", "");
      video.preload = "auto";
      video.innerHTML = '<source src="' + videoSrc + '" type="video/mp4">';
      section.appendChild(video);

      var overlay = document.createElement("div");
      overlay.className = "logo-video-overlay";
      overlay.id = "logo-video-overlay";
      section.appendChild(overlay);

      var glow = document.createElement("div");
      glow.className = "logo-video-glow";
      glow.id = "logo-video-glow";
      glow.style.backgroundImage = "url('" + logoSrc + "')";
      section.appendChild(glow);

      overlay.style.webkitMaskImage =
        "url('" + logoSrc + "'), linear-gradient(white, white)";
      overlay.style.maskImage =
        "url('" + logoSrc + "'), linear-gradient(white, white)";
    }

    var overlayEl = document.getElementById("logo-video-overlay");
    var glowEl = document.getElementById("logo-video-glow");
    var videoEl = document.getElementById("logo-video-player");
    if (!overlayEl) {
      section.classList.add("logo-video-section--ready");
      signalLogoVideoReady();
      return;
    }

    function whenVideoPlayable() {
      return new Promise(function (resolve) {
        if (!videoEl) {
          resolve();
          return;
        }
        /* HAVE_CURRENT_DATA (2) — enough to paint first frame; avoids waiting forever */
        if (videoEl.readyState >= 2) {
          resolve();
          return;
        }
        var settled = false;
        function done() {
          if (settled) return;
          settled = true;
          videoEl.removeEventListener("canplay", done);
          videoEl.removeEventListener("canplaythrough", done);
          videoEl.removeEventListener("loadeddata", done);
          videoEl.removeEventListener("error", done);
          resolve();
        }
        videoEl.addEventListener("canplay", done, { once: true });
        videoEl.addEventListener("canplaythrough", done, { once: true });
        videoEl.addEventListener("loadeddata", done, { once: true });
        videoEl.addEventListener("error", done, { once: true });
        setTimeout(done, 12000);
      });
    }

    whenVideoPlayable().then(function () {
      function refreshScrollPlugins() {
        if (typeof ScrollTrigger !== "undefined" && ScrollTrigger.refresh) {
          ScrollTrigger.refresh();
        }
        if (window._lenisInstance && typeof window._lenisInstance.resize === "function") {
          try {
            window._lenisInstance.resize();
          } catch (err) { /* no-op */ }
        }
      }

      function finishInit() {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            refreshScrollPlugins();
            if (document.readyState === "complete") {
              refreshScrollPlugins();
            } else {
              window.addEventListener(
                "load",
                function onWinLoad() {
                  window.removeEventListener("load", onWinLoad);
                  refreshScrollPlugins();
                },
                { once: true }
              );
            }
            signalLogoVideoReady();
          });
        });
      }

      /* Add .ready before creating ScrollTrigger so the section is in-flow (100vh);
         the critical CSS "boot" state must not use height:0 or pin distances stay wrong. */
      section.classList.add("logo-video-section--ready");
      if (videoEl) videoEl.play().catch(function () { });

      requestAnimationFrame(function () {
        try {
          var mobile = window.matchMedia("(max-width: 600px)").matches;
          var tablet = window.matchMedia("(max-width: 1024px)").matches;

          var START_VW = mobile ? 52 : tablet ? 38 : 24;
          var END_VW = 280;
          var SCROLL_PX = mobile ? 2200 : 3500;

          function setOverlayMaskSize(vw) {
            var logo = vw + "vw auto";
            var full = "100% 100%";
            overlayEl.style.webkitMaskSize = logo + ", " + full;
            overlayEl.style.maskSize = logo + ", " + full;
          }

          function setGlowSize(vw) {
            if (glowEl) glowEl.style.backgroundSize = vw + "vw auto";
          }

          setOverlayMaskSize(START_VW);
          setGlowSize(START_VW);

          var tl = gsap.timeline({
            scrollTrigger: {
              trigger: section,
              start: "top top",
              end: "+=" + SCROLL_PX,
              pin: true,
              scrub: 1.4,
              anticipatePin: 1,
            },
          });

          tl.to(
            { v: START_VW },
            {
              v: END_VW,
              duration: 0.4,
              ease: "power2.inOut",
              onUpdate: function () {
                var cur = this.targets()[0].v;
                setOverlayMaskSize(cur);
                setGlowSize(cur);
              },
            },
            0
          );

          tl.to(
            overlayEl,
            {
              opacity: 0,
              duration: 0.25,
              ease: "power2.in",
            },
            0.25
          );

          if (glowEl) {
            tl.to(
              glowEl,
              {
                opacity: 0,
                duration: 0.2,
                ease: "power1.in",
              },
              0.2
            );
          }

          tl.to({}, { duration: 0.5 }, 0.5);

          var mqTablet = window.matchMedia("(max-width: 1024px)");
          mqTablet.addEventListener("change", function () {
            window.location.reload();
          });
          finishInit();
        } catch (e) {
          console.warn("[logo-video] init error:", e);
          finishInit();
        }
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }
})();
